import type { InsightFamily } from "@amzn/d16-g-client-deck-builder-typescript-client";
import { ContentType } from "@amzn/g4sservice-client";
import { has, isEmpty } from "lodash";

import type { RelevantInsightsBody } from "../api/g4sApi/types/operations";
import type { InsightFromAIFieldSelections } from "../types/filters/insightFromAI";
import { InsightsFromAIFilterField } from "../types/filters/insightFromAI";
import type { InsightFamilyWithSummary } from "../types/insightFromAI";

import type { FilterOptionsMap } from "./filters.utils";

export function convertToInsightFamilyWithSummary(
    advertiserId: string | undefined,
    relevantInsightsBody: RelevantInsightsBody,
): InsightFamilyWithSummary[] {
    return relevantInsightsBody.insights.map((insight) => ({
        insightFamily: {
            identifier: insight.insightFamily.familyInstanceId,
            title:
                insight.insightFamily.textContents[0].textContent ||
                "No Content",
            advertiserId,
            brand: insight.insightFamily.brandName,
            brandId: insight.insightFamily.brandId,
            browseNode: insight.insightFamily.browseNodeName,
            browseNodeId: insight.insightFamily.browseNodeId,
        } as InsightFamily,
        g4sSummary: insight.g4sSummary,
        selectedInsightSubtypeIds:
            relevantInsightsBody.selectedInsightSubtypeIds,
        aiInsight: insight,
    }));
}

export type InsightsFromAIFilters = {
    [InsightsFromAIFilterField.CONTENT_TYPES]: FilterOptionsMap;
    [InsightsFromAIFilterField.BRAND]: FilterOptionsMap;
    [InsightsFromAIFilterField.BROWSE_NODE]: FilterOptionsMap;
};

export const matchesInsightFromAISelections = (
    insight: InsightFamily,
    fieldSelections: InsightFromAIFieldSelections,
): boolean => {
    // A match is considered true if any of the following conditions are met
    // 1. The insight has that field defined and that field matches the selected option
    // 2. The default option is selected
    // 3. The insight does not have that field

    const matchesBrand =
        (insight.brand &&
            insight.brandId &&
            fieldSelections.AI_INSIGHT_BRAND.some(
                (brand) => brand.value === insight.brandId,
            )) ||
        fieldSelections.AI_INSIGHT_BRAND.length === 0 ||
        isEmpty(insight.brandId) ||
        isEmpty(insight.brand);

    const matchesBrowseNode =
        (insight.browseNode &&
            insight.browseNodeId &&
            fieldSelections.AI_INSIGHT_BROWSE_NODE.some(
                (browseNode) => browseNode.value === insight.browseNodeId,
            )) ||
        fieldSelections.AI_INSIGHT_BROWSE_NODE.length === 0 ||
        isEmpty(insight.browseNodeId) ||
        isEmpty(insight.browseNode);

    return matchesBrand && matchesBrowseNode;
};

export const generateInsightFromAIFilterOptions = (
    insights: InsightFamily[],
): InsightsFromAIFilters => {
    const filters: InsightsFromAIFilters = {
        [InsightsFromAIFilterField.CONTENT_TYPES]: {
            [ContentType.INSIGHT]: {
                label: "Insights",
                value: ContentType.INSIGHT,
            },
            [ContentType.MARKETING_CONTENT]: {
                label: "Marketing Content",
                value: ContentType.MARKETING_CONTENT,
            },
            [ContentType.TEMPLATE]: {
                label: "Templates",
                value: ContentType.TEMPLATE,
            },
        },
        [InsightsFromAIFilterField.BRAND]: {},
        [InsightsFromAIFilterField.BROWSE_NODE]: {},
    };

    insights.forEach((insight: InsightFamily) => {
        const { brand, brandId, browseNode, browseNodeId } = insight;

        if (
            brandId &&
            brand &&
            !has(filters[InsightsFromAIFilterField.BRAND], brandId)
        ) {
            filters[InsightsFromAIFilterField.BRAND][brandId] = {
                label: brand,
                value: brandId,
            };
        }

        if (
            browseNodeId &&
            browseNode &&
            !has(filters[InsightsFromAIFilterField.BROWSE_NODE], browseNodeId)
        ) {
            filters[InsightsFromAIFilterField.BROWSE_NODE][browseNodeId] = {
                label: browseNode,
                value: browseNodeId,
            };
        }
    });

    return filters;
};
